/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { QnaMakerAzureSearchCard } from 'src/qnaMaker/appShell/qnaMakerAzureSearchPage/components/QnaMakerAzureSearchCard';
import { QnaMakerAzureSearchCommandBar } from 'src/qnaMaker/appShell/qnaMakerAzureSearchPage/components/QnaMakerAzureSearchCommandBar';
import { ServiceCreationModal } from 'src/qnaMaker/appShell/qnaMakerAzureSearchPage/components/QnaMakerAzureSearchServiceCreationModal';
import { ServiceSwitchingModal } from 'src/qnaMaker/appShell/qnaMakerAzureSearchPage/components/QnaMakerAzureSearchServiceSwitchingModal';

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.qnaMakerAzureSearchPage.pageTitle',
        defaultMessage: 'Azure Search'
    },
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerAzureSearchPage.emptyStateMessage',
        defaultMessage: 'Select an Azure Search resource to get started'
    }
});

export const QnaMakerAzureSearchPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const tracker = useProgressTracker();

    const azureSession = rootStore.azureStore.session;

    const isResourceConnectedToAzureSearch =
        Boolean(azureSession.subscriptionId) &&
        Boolean(azureSession.csAccountStore?.path) &&
        azureSession.csAccountStore?.kind === 'TextAnalytics' &&
        Boolean(azureSession.csAccountStore?.ownedSearchResourceId);

    const { subscriptionId } = rootStore.azureStore.session;

    React.useEffect(() => dispatcher.dispatch('getAzureSearchResources', { tracker, id: subscriptionId }), [subscriptionId]);

    const defaultModalsState = { isCreateServiceModalHidden: true, isSwitchServiceModalHidden: true };
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);

    const [selectedService, setSelectedService] = React.useState(null);

    return (
        <Page size="container-fluid" pageName="QnaMakerAzureSeachPage" title={localize(messages.pageTitle)}>
            <Stack tokens={{ childrenGap: 25 }} verticalFill>
                <LuisText as="h1" variant="xLargePlus" fontWeight={600}>
                    {localize(messages.pageTitle)}
                </LuisText>

                <QnaMakerAzureSearchCommandBar
                    onCreateAzureSearchService={() => setModalsState({ isCreateServiceModalHidden: false })}
                    onChangeAzureSearchService={(path: string) => {
                        setSelectedService(path);
                        setModalsState({ isSwitchServiceModalHidden: false });
                    }}
                    isLoading={tracker.isRunning}
                />

                {isResourceConnectedToAzureSearch ? (
                    <QnaMakerAzureSearchCard isLoading={tracker.isRunning} />
                ) : (
                    <EmptyStateView message={localize(messages.emptyStateMessage)} />
                )}
            </Stack>

            <ServiceCreationModal
                hidden={modalsState.isCreateServiceModalHidden}
                onDismiss={() => setModalsState({ isCreateServiceModalHidden: true })}
                onChangeAzureSearchService={(path: string) => {
                    setSelectedService(path);
                    setModalsState({ isSwitchServiceModalHidden: false });
                }}
            />

            <ServiceSwitchingModal
                servicePath={selectedService}
                hidden={modalsState.isSwitchServiceModalHidden}
                onDismiss={() => setModalsState({ isSwitchServiceModalHidden: true })}
            />
        </Page>
    );
});
